// Страница публичных лайфхаков: /lifehacks.
//
// Читают её без входа — это витрина, и половина читателей приходит из поиска.
// Пишет любой вошедший, но написанное попадает не на страницу, а в очередь к
// админу. Отсюда две вещи, которые видно в разметке:
//
// 1. Форма стоит над списком, а не под ним. Человек, дочитавший до конца
//    списка, уже уходит; предложить дописать своё надо там, где он ещё читает.
// 2. Свои неопубликованные видны только автору и только по отдельной кнопке.
//    Иначе он напишет совет второй раз, решив, что первый не сохранился, — а
//    показывать очередь в общем списке нельзя: это и есть обход модерации.
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactElement,
} from 'react';
import { LANG_META, useI18n, useT } from '../i18n';
import { Logo } from '../ui/shell';
import { AccountButton, useAuth } from '../auth';
import { useDocumentMeta } from '../seo';
import {
  createLifehack,
  deleteLifehack,
  fetchLifehacks,
  setConfirm,
  type Lifehack,
  type LifehackList,
} from './api';
import { CATEGORIES, MAX_TEXT, MIN_TEXT, sortedCountries, countryNamer } from './catalog';
import styles from './LifehacksPage.module.css';

/**
 * Коды ошибок этой страницы в ключи словаря.
 *
 * Свой словарь, а не общий `errorKey` из auth: там живут коды авторизации, и
 * дописывать в него «слишком длинный текст» значит смешивать две разные
 * области. Неизвестный код падает в общую фразу — она в auth и остаётся.
 */
const OWN_ERRORS = new Set([
  'text_short',
  'text_long',
  'bad_country',
  'bad_category',
  'too_many_pending',
  'rate_limited',
]);

function lifehackErrorKey(code: string | undefined): string {
  return code && OWN_ERRORS.has(code) ? `lh.err.${code}` : 'auth.err.unknown';
}

/**
 * Название языка на нём самом — «English», «Ελληνικά».
 *
 * Не переводится на язык читателя намеренно: подпись стоит на совете, которого
 * читатель на своём языке не получил, и «английский» он опознаёт хуже, чем
 * «English», — а на карточке нужен именно быстрый ответ «а это на чём?».
 * Неизвестный код показываем как есть: в базе может лежать язык, который мы
 * ещё не поддерживаем в интерфейсе.
 */
function langName(code: string): string {
  const meta = (LANG_META as Record<string, { name: string } | undefined>)[code];
  return meta?.name ?? code;
}

/** Выпадашка стран: одна и та же в форме и в фильтре, отличается подписью. */
function CountrySelect({
  id,
  value,
  anyLabel,
  onChange,
}: {
  id: string;
  value: string;
  anyLabel: string;
  onChange(next: string): void;
}): ReactElement {
  const { lang } = useI18n();
  // Двести пятьдесят названий и их сортировка по языку — не та работа, которую
  // стоит делать на каждый ввод буквы в поле текста рядом.
  const countries = useMemo(() => sortedCountries(lang), [lang]);

  return (
    <select
      id={id}
      className={styles.select}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{anyLabel}</option>
      {countries.map((c) => (
        <option key={c.code} value={c.code}>
          {c.name}
        </option>
      ))}
    </select>
  );
}

function LifehackForm({ onAdded }: { onAdded(): void }): ReactElement {
  const t = useT();
  const { lang } = useI18n();
  const { user, requestSignIn } = useAuth();

  const [text, setText] = useState('');
  const [country, setCountry] = useState('');
  const [category, setCategory] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  if (!user) {
    return (
      <section className={styles.form}>
        <p className={styles.formLede}>{t('lh.form.guest')}</p>
        <button type="button" className="btn btn-primary" onClick={requestSignIn}>
          {t('auth.signin')}
        </button>
      </section>
    );
  }

  const trimmed = text.trim();
  const tooShort = trimmed.length < MIN_TEXT;
  const left = MAX_TEXT - trimmed.length;

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (busy || tooShort || left < 0) return;
    setBusy(true);
    setError(null);
    // Язык интерфейса едет вместе с текстом: на нём человек и писал, а сервер
    // без подсказки посчитал бы совет английским — и переводить его потом стали
    // бы не с того языка.
    const res = await createLifehack({ text: trimmed, country, category, lang });
    setBusy(false);
    if (!res.ok) {
      setError(lifehackErrorKey(res.error.error));
      return;
    }
    // Поле очищается, а страна и категория остаются: следующий совет про ту же
    // поездку обычно про ту же страну, и выбирать её заново — лишняя работа.
    setText('');
    setSent(true);
    onAdded();
  }

  return (
    <form className={styles.form} onSubmit={(e) => void submit(e)}>
      <label className={styles.label} htmlFor="lh-text">
        {t('lh.form.text')}
      </label>
      <textarea
        id="lh-text"
        className={styles.textarea}
        value={text}
        rows={3}
        maxLength={MAX_TEXT * 2}
        placeholder={t('lh.form.placeholder')}
        onChange={(e) => {
          setText(e.target.value);
          setSent(false);
        }}
      />

      <div className={styles.formRow}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="lh-category">
            {t('lh.form.category')}
          </label>
          <select
            id="lh-category"
            className={styles.select}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            {/* «Не выбрано», а не «Все категории»: в форме пустой пункт значит
                «я не отношу это ни к чему», а в фильтре — «показать всё». Одно
                слово на оба места врало бы в одном из них. */}
            <option value="">{t('lh.form.none')}</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {t(`lh.cat.${c}`)}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="lh-country">
            {t('lh.form.country')}
          </label>
          <CountrySelect
            id="lh-country"
            value={country}
            anyLabel={t('lh.form.none')}
            onChange={setCountry}
          />
        </div>
      </div>

      <div className={styles.formFoot}>
        {/* Счётчик появляется только на подходе к пределу: постоянный счётчик
            над коротким полем читается как требование написать много. */}
        <span className={styles.counter} aria-live="polite">
          {left <= 100 ? t('lh.form.left', { count: left }) : ''}
        </span>
        <button type="submit" className="btn btn-primary" disabled={busy || tooShort || left < 0}>
          {t('lh.form.submit')}
        </button>
      </div>

      {error && <p className={styles.error}>{t(error)}</p>}
      {sent && !error && <p className={styles.sent}>{t('lh.form.sent')}</p>}
      <p className={styles.formHint}>{t('lh.form.hint')}</p>
    </form>
  );
}

function LifehackCard({
  item,
  canDelete,
  countryName,
  onConfirm,
  onDelete,
}: {
  item: Lifehack;
  canDelete: boolean;
  /** Приезжает сверху готовым: `Intl.DisplayNames` на каждую карточку — сотня
   *  одинаковых объектов на список из сотни советов. */
  countryName(code: string): string;
  onConfirm(next: boolean): void;
  onDelete(): void;
}): ReactElement {
  const t = useT();
  const { lang } = useI18n();
  const { user, requestSignIn } = useAuth();
  const country = countryName(item.country);
  const date = useMemo(
    () => new Intl.DateTimeFormat(lang, { dateStyle: 'medium' }).format(item.createdAt),
    [lang, item.createdAt],
  );

  return (
    <article className={styles.card}>
      <p className={styles.text}>{item.text}</p>

      <div className={styles.meta}>
        {item.category && <span className={styles.tag}>{t(`lh.cat.${item.category}`)}</span>}
        {country && <span className={styles.tag}>{country}</span>}
        {item.status !== 'published' && (
          <span className={`${styles.tag} ${styles.tagStatus}`}>{t(`lh.status.${item.status}`)}</span>
        )}
        {/* У редакционного совета вместо имени автора — знак «от редакции».
            Имя служебного аккаунта читателю ничего не говорит, а знак отвечает
            на единственный вопрос, который у него есть: кто это написал. */}
        {item.source === 'editorial' ? (
          <span className={`${styles.tag} ${styles.tagEditorial}`}>{t('lh.editorial')}</span>
        ) : (
          <span className={styles.author}>{item.authorName.trim() || t('lh.author.anon')}</span>
        )}
        {/* Перевода на язык интерфейса не нашлось — говорим об этом подписью, и
            только ей: подменять текст заглушкой «перевода нет» значило бы
            спрятать совет, который человек всё-таки может прочитать. */}
        {item.lang !== lang && (
          <span className={styles.langNote} title={t('lh.lang.hint')}>
            {langName(item.lang)}
          </span>
        )}
        <span className={styles.date}>{date}</span>

        <span className={styles.metaGap} />

        {canDelete && (
          <button type="button" className={styles.linkBtn} onClick={onDelete}>
            {t('lh.delete')}
          </button>
        )}

        {/* Гостю кнопка видна и кликается: она приводит ко входу, а не молча
            ничего не делает. Спрятать её значило бы скрыть, что подтверждать
            вообще можно. */}
        <button
          type="button"
          className={`${styles.confirm} ${item.confirmed ? styles.confirmOn : ''}`}
          aria-pressed={user ? item.confirmed : undefined}
          title={t(item.confirmed ? 'lh.confirmed' : 'lh.confirm')}
          onClick={() => (user ? onConfirm(!item.confirmed) : requestSignIn())}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span className={styles.confirmCount}>{item.confirms}</span>
        </button>
      </div>
    </article>
  );
}

export function LifehacksPage(): ReactElement {
  const t = useT();
  const { lang } = useI18n();
  const { user } = useAuth();

  const [country, setCountry] = useState('');
  const [category, setCategory] = useState('');
  const [mine, setMine] = useState(false);
  const [list, setList] = useState<LifehackList>({
    items: [],
    facets: { countries: [], categories: [] },
  });
  const [loading, setLoading] = useState(true);

  useDocumentMeta({ title: t('lh.seo.title'), description: t('lh.seo.description') });

  // Смена языка интерфейса перезапрашивает список: тексты переводит сервер, и
  // без нового запроса на экране остались бы старые — русская страница с
  // английскими советами.
  const reload = useCallback(async () => {
    setLoading(true);
    setList(await fetchLifehacks({ country, category, mine, lang }));
    setLoading(false);
  }, [country, category, mine, lang]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Выход из аккаунта на открытой странице оставил бы включённым «мои»:
  // список тогда молча стал бы общим, а переключатель — врущим.
  useEffect(() => {
    if (!user) setMine(false);
  }, [user]);

  /** Ответ сервера уже содержит новое число подтверждений — заменяем строку
   *  на месте, а не перезапрашиваем весь список: порядок в нём зависит от
   *  подтверждений, и список прыгнул бы под пальцем. */
  async function confirm(item: Lifehack, next: boolean): Promise<void> {
    const res = await setConfirm(item.id, next, lang);
    if (!res.ok) return;
    const fresh = res.value.item;
    setList((prev) => ({
      ...prev,
      items: prev.items.map((row) => (row.id === fresh.id ? fresh : row)),
    }));
  }

  async function remove(item: Lifehack): Promise<void> {
    const res = await deleteLifehack(item.id);
    if (!res.ok) return;
    setList((prev) => ({ ...prev, items: prev.items.filter((row) => row.id !== item.id) }));
  }

  const countryName = useMemo(() => countryNamer(lang), [lang]);
  const shownCategories = useMemo(
    () => new Set(list.facets.categories.map((f) => f.value)),
    [list.facets.categories],
  );

  return (
    <div className={styles.page}>
      {/* Шапка та же, что на «Моих поездках»: знак, заголовок и кнопка аккаунта
          справа. Бургер здесь стоял ради набора ссылок, который целиком есть в
          меню самой кнопки (язык, поездки, тарифы, персонализация, вход и
          выход), — и выходило, что на соседних экранах одно и то же лежит в
          разных местах. Возврат в планировщик даёт знак и ссылка в подвале,
          так что и он ничего не теряет. */}
      <header className={styles.header}>
        <Logo title={t('app.brand')} href="/" />
        <h1 className={styles.title}>{t('lh.title')}</h1>
        <AccountButton />
      </header>

      <p className={styles.lede}>{t('lh.subtitle')}</p>

      <LifehackForm onAdded={() => void reload()} />

      <div className={styles.filters}>
        {/* Категории — кнопками, а не выпадашкой: их пять, и переключение
            между ними должно стоить одно нажатие. Страны — выпадашкой: их
            двести пятьдесят. */}
        <div className={styles.chips} role="group" aria-label={t('lh.filter.category')}>
          <button
            type="button"
            className={`${styles.chip} ${category === '' ? styles.chipOn : ''}`}
            aria-pressed={category === ''}
            onClick={() => setCategory('')}
          >
            {t('lh.any.category')}
          </button>
          {CATEGORIES.filter((c) => shownCategories.has(c) || c === category).map((c) => (
            <button
              key={c}
              type="button"
              className={`${styles.chip} ${category === c ? styles.chipOn : ''}`}
              aria-pressed={category === c}
              onClick={() => setCategory(c)}
            >
              {t(`lh.cat.${c}`)}
            </button>
          ))}
        </div>

        <div className={styles.filterRight}>
          {/* В фильтре предлагаются только страны, по которым есть что
              показать: выбор, дающий пустой список, — это обещание, которого
              страница не держит. */}
          <select
            className={styles.select}
            aria-label={t('lh.filter.country')}
            value={country}
            onChange={(e) => setCountry(e.target.value)}
          >
            <option value="">{t('lh.any.country')}</option>
            {list.facets.countries.map((f) => (
              <option key={f.value} value={f.value}>
                {`${countryName(f.value)} (${f.count})`}
              </option>
            ))}
          </select>

          {user && (
            <button
              type="button"
              className={`${styles.chip} ${mine ? styles.chipOn : ''}`}
              aria-pressed={mine}
              onClick={() => setMine((on) => !on)}
            >
              {t('lh.filter.mine')}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <p className={styles.empty}>{t('lh.loading')}</p>
      ) : list.items.length === 0 ? (
        <p className={styles.empty}>{t(mine ? 'lh.empty.mine' : 'lh.empty')}</p>
      ) : (
        <div className={styles.list}>
          {list.items.map((item) => (
            <LifehackCard
              key={item.id}
              item={item}
              countryName={countryName}
              canDelete={Boolean(user) && (user?.id === item.authorId || user?.role === 'admin')}
              onConfirm={(next) => void confirm(item, next)}
              onDelete={() => void remove(item)}
            />
          ))}
        </div>
      )}

      <footer className={styles.footer}>
        <a className={styles.back} href="/">
          {t('lh.back')}
        </a>
      </footer>
    </div>
  );
}
